import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DownOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons'
import { core } from '@musinsa/pbo-core'
import { notice } from '@/components/pbo/notice'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import Loader from '@/components/content/Loader'
import { canSelectShipper, defaultShipper, scopedShipperCode, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { scopeSelectOptions } from '@/components/system/shipperScopeOptions'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { GeneralInboundParams } from '../queries/useGetInboundList'
import { ORD_OPT_NO_MAX } from './consts'

// ───── 컨덕터 조회영역 로컬 헬퍼 — 산출물(`../v2/page.tsx`, emit-hash 2a74bcd8, pbo-core 0.8.0 재핸드오프 2026-09-10) **사본**. 손보지 말 것 —
//       바꿀 게 있으면 컨덕터(emitFilterField)를 고치고 재핸드오프로 받는다. 산출물이 화면마다 한 벌씩 내보내는 규약 그대로 이 파일에 둔다.
const chipCodes = (v: unknown): string | null => (Array.isArray(v) && v.length ? `${(v as string[])[0]}${v.length > 1 ? ` 외 ${v.length - 1}개` : ''}` : null)

const chipSelect = (opts: { value: string; label: string }[], v: unknown, required?: boolean): string | null =>
  v == null || v === '' ? (required ? '전체' : null) : (opts.find((o) => o.value === v)?.label ?? String(v))

/** 조회영역 필드 프레임(그룹 cmp.FilterField) — 접힘이면 값 칩(값 없으면 숨김), 열림이면 DS FormItem(라벨 위). 규격 = pbo-core filter-layout.css .pbo-filter-field */
function FilterField({ collapsed, label, required, tooltip, width, chip, children }: { collapsed: boolean; label?: string; required?: boolean; tooltip?: string; width?: number | string; chip?: string | null; children?: ReactNode }) {
  if (collapsed) {
    return (
      <div className="pbo-filter-closed" hidden={chip == null}>
        {label ? (
          <div className="pbo-filter-chip-label">
            <label>{label}</label>
            {required ? <span className="pbo-filter-req">*</span> : null}
          </div>
        ) : null}
        <div className="pbo-filter-chip-value">{chip}</div>
      </div>
    )
  }
  return (
    <core.FormItem className="pbo-filter-field" label={label} required={required} tooltip={!!tooltip} tooltipText={tooltip} labelPosition="top" boxedItem={false} style={width != null ? { width } : undefined}>
      {children}
    </core.FormItem>
  )
}

/** 다건 코드 입력 — 인풋 + 우측 [+] 팝오버 textarea(Enter 로 다중 입력, 최대 `limit` — 기본 1,500). 규격 = .pbo-filter-code*
 *  dev 보정 2건(리뷰 2026-09-11, 컨덕터 emitFilterField 반영 요청 — codegen gaps #22·#23): ① 인풋 구분자를 쉼표만 → 쉼표·공백·줄바꿈(1.0 동치)
 *  ② 한도를 prop 으로 받아 카운터·Enter 차단이 화면 한도를 따른다(산출물은 1,500 고정). 산출물은 normalize 가 한도로 **잘라서** 내보냈는데,
 *     그러면 초과분이 소리 없이 버려져 화면 쪽 한도 경고(있는 화면에서)가 죽은 코드가 되고 카운터 빨강도 켜질 수 없다(리뷰 Critical 2026-09-11) — 자르지 않고 그대로 내보낸다. */
function MultiCodeInput({ value, onChange, placeholder, limit = 1500 }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; limit?: number }) {
  const [open, setOpen] = useState(false)
  // 편집 원문 두 벌(인풋=쉼표·팝오버=줄) — 밖으로 내는 배열만 정규화한다. 입력마다 정규화해 되돌려 쓰면 끝의 구분자가 즉시 지워진다.
  const [text, setText] = useState(value.join('\n'))
  const [line, setLine] = useState(value.join(', '))
  const wrapRef = useRef<HTMLDivElement>(null)
  const joined = value.join('\n')
  const normalize = (raw: string, splitBy: RegExp) => Array.from(new Set(raw.split(splitBy).map((s) => s.trim()).filter(Boolean)))
  // 팝오버도 인풋과 같은 구분자 — 같은 문자열이 두 입력 경로에서 다르게 해석되지 않게(리뷰 2026-09-11).
  const shown = normalize(text, /[\s,]+/)
  // 밖에서 값이 바뀌었을 때만(초기화·복원) 원문을 따라간다 — 내 입력이 만든 값이면 원문(끝의 구분자 포함)을 보존한다.
  const lastEmit = useRef(joined)
  useEffect(() => {
    if (joined === lastEmit.current) return
    lastEmit.current = joined
    setText(joined)
    setLine(value.join(', '))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined])
  const emit = (arr: string[]) => {
    lastEmit.current = arr.join('\n')
    onChange(arr)
  }
  const onLine = (raw: string) => {
    setLine(raw)
    // 쉼표·공백·줄바꿈 아무거나로 끊는다(1.0 코드 입력 동치 — 엑셀 열을 인풋에 붙여 넣어도 한 건으로 합쳐지지 않게).
    const arr = normalize(raw, /[\s,]+/)
    setText(arr.join('\n'))
    emit(arr)
  }
  const onText = (raw: string) => {
    setText(raw)
    const arr = normalize(raw, /[\s,]+/)
    setLine(arr.join(', '))
    emit(arr)
  }
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])
  return (
    <div className="pbo-filter-code" ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <core.Input
        placeholder={placeholder ?? '다건 입력 시, 우측 + 버튼을 눌러주세요.'}
        value={line}
        onChange={(e) => onLine(e.target.value)}
        suffix={<PlusOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, cursor: 'pointer' }} onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); setOpen(true) }} />}
      />
      {open && (
        <div className="pbo-filter-code-pop">
          <core.Input.TextArea
            value={text}
            placeholder="입력 후 Enter 키로 다중 입력이 가능합니다."
            onChange={(e) => onText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && shown.length >= limit) e.preventDefault() }}
            autoSize={{ minRows: 4, maxRows: 16 }}
            autoFocus
          />
          <div className="pbo-filter-code-foot">
            <span style={{ fontSize: 12, color: shown.length > limit ? '#ff4d4f' : '#8c8c8c' }}>{shown.length} / {limit.toLocaleString()}</span>
            <core.Button size="small" onClick={() => onText('')}>
              초기화
            </core.Button>
          </div>
        </div>
      )}
    </div>
  )
}
// ───── 헬퍼 끝

export interface ShipperOption {
  /** shipperCode — 2.0 조회 파라미터 */
  value: string
  /** shipperName — 표시용 */
  label: string
}

interface Props {
  shipperOptions: ShipperOption[]
  /** 재진입 복원 검색조건(menuOnSession → URL 쿼리) — 화주·입고번호 모두 필드에 되살린다(searchQuery.ts 표 참조). */
  initialParams?: GeneralInboundParams
  /** 복원된 목록 검색어 — 최초 자동 조회에서 그대로 실어 URL 이 덮이지 않게 한다(결정 12) */
  initialListQuery?: string
  onSearch: (params: GeneralInboundParams, keepListQuery?: string, forceRefetch?: boolean) => void
  onReset: () => void
}

/**
 * GR01 일반입고 조회영역 — 컨덕터 `cmp.FilterCard` 산출물(`v2/page.tsx` #inboundFilterCard, emit-hash 664fb893) 구조 그대로.
 *
 * `core.Card > div(.pbo-filter-collapse-open|closed) > [Flex(20, wrap) 필드 2 · Flex(8, end) 푸터]`.
 * 필드 프레임은 `FilterField`(열림 = `core.FormItem` 라벨 위 · 접힘 = 값 칩), 컨트롤은 요소 조합이다 —
 * 화주명 `core.Select`(필수·'전체' 없음) · 입고번호 `MultiCodeInput`. DS 옛 통짜 필터 조합은 범위 게이트가 금지한다.
 * 헬퍼는 이 파일 위쪽의 산출물 사본이다.
 *
 * 값은 전부 **제어 컴포넌트**로 이 컴포넌트 state 에 산다(산출물의 값 버스 `flInboundFilterCard` 와 같은 역할).
 * 기본 화주 = 현재 세션 화주(shipper_code — 화주 전환을 반영한다, LOGISPRD-14651) — 1.0 GR01Container 가 userInfo.company(전환 반영)를 쓰던 동작과 같다(데이터 계약).
 * 입고번호 다건은 BE @Size(max=200) — 초과하면 경고하고 조회하지 않는다(인풋 한도 표기도 같은 값).
 */
const GeneralInboundFilterCard = ({ shipperOptions, initialParams, initialListQuery = '', onSearch, onReset, coMingle }: Readonly<Props & { coMingle: boolean }>) => {
  const user = useMemo(() => AuthStorage.user.get(), [])
  // 화주 잠금(LOGISPRD-14653) — 통합 운영 화주 외 계정은 화주명 옵션이 자기 화주 하나고, 복원·조회 화주도 자기 화주로 강제한다(1.0 FilterBar 규칙).
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15608, `withCoMingle`).
  const scope = useMemo(() => withCoMingle(shipperScopeOf(user), coMingle), [user, coMingle])
  // 기본 화주 = 현재 세션 화주(shipper_code — 화주 전환을 반영한다, LOGISPRD-14651). 재고통합 대상 계정은 MUSINSA 다(LOGISPRD-15718).
  //  통합 운영 계정은 옵션에 없으면 첫 화주로 폴백한다.
  const defaultShipperCode = defaultShipper(scope).shipperCode
  const optionShipper = useMemo(() => (shipperOptions.find((o) => o.value === defaultShipperCode) ?? shipperOptions[0])?.value, [shipperOptions, defaultShipperCode])
  const baseShipper = scope.locked ? defaultShipperCode : optionShipper
  // 잠금 계정은 복원 화주가 고를 수 없는 화주면 기본 화주로 덮는다.
  const restored = useMemo(() => (scope.locked && initialParams ? { ...initialParams, shipperCode: scopedShipperCode(scope, initialParams.shipperCode) } : initialParams), [scope, initialParams])
  const initialShipper = restored?.shipperCode || baseShipper

  const [collapsed, setCollapsed] = useState(false)
  const [shipperCode, setShipperCode] = useState<string | undefined>(initialShipper)
  // 복원된 입고번호도 필드에 세운다 — 초기 자동 조회가 이 값으로 걸러진 목록을 내놓는데 칸이 비어 있으면
  // 사용자는 왜 이것만 나오는지 알 수 없다(옛 FilterCodeInput 은 초기값을 못 받았지만 지금 입력은 제어형이다).
  const [ordOptionNumbers, setOrdOptionNumbers] = useState<string[]>(initialParams?.ordOptionNumbers ?? [])

  const selectOptions = useMemo(() => scopeSelectOptions(shipperOptions.map(({ value, label }) => ({ value, label })), scope), [shipperOptions, scope])

  // 화주 옵션이 비동기라 마운트 시점엔 비어 있을 수 있다 — 옵션이 도착해 기본 화주가 정해지면 한 번 따라간다.
  useEffect(() => {
    if (!shipperCode && initialShipper) setShipperCode(initialShipper)
  }, [initialShipper, shipperCode])

  // 초기 진입 1회 — 복원 조건이 있으면 그 값으로, 없으면 기본 화주 기준 자동 조회 (1.0 필수 필터 동작 보존)
  const didInit = useRef(false)
  useEffect(() => {
    if (didInit.current || !baseShipper) return
    didInit.current = true
    // 복원 진입이라 재조회를 강제하지 않는다 — 캐시가 살아 있으면 그대로 그려진다(결정 12).
    onSearch(restored ?? { shipperCode: baseShipper }, initialListQuery, false)
  }, [baseShipper, onSearch, restored, initialListQuery])

  const handleSearch = useCallback(() => {
    // 화주명은 필수 필터다. 옵션 로드 타이밍 등으로 빈 값이면 기본 화주로 되돌려 채운다 — 화주가 통째로 빠진 조회를 막는다.
    const picked = shipperCode || baseShipper
    // 잠금 계정은 고를 수 있는 화주만 조회한다 — 그 밖의 값은 기본 화주로 덮는다.
    const code = scope.locked ? scopedShipperCode(scope, picked) : picked
    if (!code) return
    if (!shipperCode) setShipperCode(code)
    // 입고번호 상한(BE @Size(max=200)) — 넘치면 잘라 보내지 않고 경고하고 조회하지 않는다(리뷰 지적 2026-09-11: 201번째부터 조용히 빠지던 것).
    if (ordOptionNumbers.length > ORD_OPT_NO_MAX) {
      notice.invalid(`입고번호는 최대 ${ORD_OPT_NO_MAX}개까지 입력할 수 있습니다.`)
      return
    }
    onSearch({ shipperCode: code, ordOptionNumbers: ordOptionNumbers.length ? ordOptionNumbers : undefined }, initialListQuery)
  }, [shipperCode, baseShipper, scope, ordOptionNumbers, initialListQuery, onSearch])

  const handleReset = useCallback(() => {
    // 화주명은 필수라 기본 화주로 되살린다.
    setShipperCode(baseShipper)
    setOrdOptionNumbers([])
    onReset()
  }, [baseShipper, onReset])

  return (
    <core.Card styles={{ body: { padding: '12px 20px' } }} style={{ border: '1px solid #d9d9d9', borderRadius: 8, minWidth: 0 }}>
      <div className={`${collapsed ? 'pbo-filter-collapse-closed' : 'pbo-filter-collapse-open'}`}>
        {collapsed && <span className="pbo-filter-empty-msg">필터가 적용된 항목이 없어요. 필터를 열어 원하는 조건으로 검색해 보세요.</span>}
        <core.Flex gap={20} align="flex-start" wrap>
          <FilterField collapsed={collapsed} label="화주명" required chip={chipSelect(selectOptions, shipperCode ?? '', true)}>
            <core.Select
              className="pbo-filter-select"
              showSearch
              optionFilterProp="label"
              options={selectOptions}
              value={shipperCode}
              onChange={(v) => setShipperCode(String(v ?? '') || baseShipper)}
              disabled={!canSelectShipper(scope)} // 화주 고정 계정은 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 고를 화주가 둘 이상이면 연다(LOGISPRD-15608). 값은 baseShipper·restored·handleSearch 가 고를 수 있는 화주로 고정한다
              placeholder="화주명을(를) 선택해주세요."
              style={{ width: '100%' }}
            />
          </FilterField>
          <FilterField collapsed={collapsed} label="입고번호" chip={chipCodes(ordOptionNumbers)}>
            <MultiCodeInput limit={ORD_OPT_NO_MAX} value={ordOptionNumbers} onChange={setOrdOptionNumbers} />
          </FilterField>
        </core.Flex>
        <core.Flex gap={8} justify="flex-end" align="center">
          <core.Button icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={() => setCollapsed((c) => !c)}>{`필터 ${collapsed ? '열기' : '닫기'}`}</core.Button>
          <span style={{ height: 18, margin: '0 0px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />
          <core.Button onClick={handleReset}>초기화</core.Button>
          <core.Button type="primary" onClick={handleSearch}>
            조회
          </core.Button>
        </core.Flex>
      </div>
    </core.Card>
  )
}

/**
 * 재고통합 FF 첫 조회를 기다린 뒤 조회영역을 연다 — 화주 범위·복원 화주·첫 자동 조회가 마운트 때 정해지므로
 * FF 값이 정해지기 전에 만들면 잠긴 29CM·INOOINT 의 MUSINSA 복원이 자기 화주로 덮인다(LOGISPRD-15608).
 */
export const GeneralInboundFilter = (props: Readonly<Props>) => {
  const { ready, coMingle } = useCoMingleShipperScope()
  if (!ready) return <Loader />
  return <GeneralInboundFilterCard {...props} coMingle={coMingle} />
}

export default GeneralInboundFilter
